import { memo, useCallback, useState } from 'react';
import './copy-id-button.css';

interface CopyIdButtonProps {
  /** Value to write to the clipboard. */
  value: string;
  /** What the user is copying — used in the aria-label and the toast text. */
  label?: string;
  /** Visual variant: inline next to text, or icon-only. */
  variant?: 'inline' | 'icon';
  /** Override the title prefix. */
  className?: string;
  /** Stop propagation so click doesn't trigger surrounding card handlers. */
  stopPropagation?: boolean;
}

/**
 * Tap-friendly button that copies a value to the clipboard and shows a brief
 * "Copied" confirmation. Falls back to a hidden textarea when
 * `navigator.clipboard` isn't available (older Safari, insecure context).
 */
export const CopyIdButton = memo(function CopyIdButton({
  value,
  label = 'ID',
  variant = 'inline',
  className,
  stopPropagation = true,
}: CopyIdButtonProps) {
  const [copied, setCopied] = useState(false);

  const handleClick = useCallback(
    async (e: React.MouseEvent) => {
      if (stopPropagation) e.stopPropagation();
      e.preventDefault();
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(value);
        } else {
          const ta = document.createElement('textarea');
          ta.value = value;
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      } catch {
        // Silently fail — user can still long-press to copy.
      }
    },
    [value, stopPropagation],
  );

  const cls = `copy-id-btn copy-id-btn--${variant}${copied ? ' is-copied' : ''}${
    className ? ' ' + className : ''
  }`;

  return (
    <button
      type="button"
      className={cls}
      onClick={handleClick}
      aria-label={copied ? `${label} copied` : `Copy ${label}`}
      title={copied ? `${label} copied` : `Copy ${label}`}
    >
      <span className="material-symbols-outlined" aria-hidden="true">
        {copied ? 'check' : 'content_copy'}
      </span>
      {variant === 'inline' && (
        <span className="copy-id-btn-text">{copied ? 'Copied' : 'Copy'}</span>
      )}
    </button>
  );
});

export default CopyIdButton;
